/**
 * ConversationInitiator Component
 * Phase 5: Session Management UI (2026-01-19)
 *
 * Allows starting new WhatsApp conversations using templates.
 * Handles template selection, variable input, and contact selection.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Send,
  Person,
  Message,
  Description,
  Check,
  Phone,
  ArrowBack,
  ArrowForward,
  ContentCopy,
  Preview
} from '@mui/icons-material';
import api from '../../services/api';

interface Contact {
  id: number;
  name: string;
  phone_number: string;
  email?: string;
}

interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  // components: any[];
  components: {
    header?: any;
    body?: string;
    footer?: string;
    buttons?: any[];
  };  // Changed from any[] to object
  variables: string[];
  approval_status: string;
}

interface ConversationInitiatorProps {
  open: boolean;
  onClose: () => void;
  onConversationCreated: (conversationId: number) => void;
  preselectedContact?: Contact;
  preselectedTemplate?: WhatsAppTemplate;
}

const steps = ['Select Contact', 'Choose Template', 'Configure Message', 'Send'];

const ConversationInitiator: React.FC<ConversationInitiatorProps> = ({
  open,
  onClose,
  onConversationCreated,
  preselectedContact,
  preselectedTemplate
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Step 1: Contact
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(preselectedContact || null);
  const [contactSearch, setContactSearch] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');

  // Step 2: Template
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(preselectedTemplate || null);
  const [templateCategory, setTemplateCategory] = useState<string>('');

  // Step 3: Variables
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [messagePreview, setMessagePreview] = useState('');

  // Fetch contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/contacts/', { params: { search: contactSearch } });
        setContacts(response.data.contacts || response.data || []);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchContacts();
    }
  }, [open, contactSearch]);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const params: any = {};
        if (templateCategory) {
          params.category = templateCategory;
        }
        const response = await api.get('/campaigns/whatsapp-templates/', { params });
        setTemplates(response.data.templates || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    if (open && activeStep >= 1) {
      fetchTemplates();
    }
  }, [open, activeStep, templateCategory]);

  // Update message preview when template or variables change
  useEffect(() => {
    if (selectedTemplate) {
      // Fix: components is an object, not an array
      // API returns: { header: {...}, body: "...", footer: "...", buttons: [...] }
      const bodyText = selectedTemplate.components?.body;
      if (bodyText) {
        let preview = bodyText;
        // Replace variables
        Object.entries(variables).forEach(([key, value]) => {
          preview = preview.replace(`{{${key}}}`, value || `[${key}]`);
        });
        setMessagePreview(preview);
      } else {
        setMessagePreview('');
      }
    } else {
      setMessagePreview('');
    }
  }, [selectedTemplate, variables]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedContact(preselectedContact || null);
      setSelectedTemplate(preselectedTemplate || null);
      setVariables({});
      setNewPhoneNumber('');
    }
  }, [open, preselectedContact, preselectedTemplate]);

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSendMessage();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedContact !== null || newPhoneNumber.trim().length >= 10;
      case 1:
        return selectedTemplate !== null;
      case 2:
        // All required variables must be filled
        if (!selectedTemplate) return false;
        // Fix: Ensure variables is an array
        const templateVars = Array.isArray(selectedTemplate.variables) 
          ? selectedTemplate.variables 
          : [];
        return templateVars.every(
          (_, index) => variables[`${index + 1}`]?.trim()
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!selectedTemplate) return;

    try {
      setSending(true);

      const phoneNumber = selectedContact?.phone_number || newPhoneNumber;

      // Create/get conversation and send template message
      const response = await api.post('/whatsapp/templates/send', {
        phone_number: phoneNumber,
        template_name: selectedTemplate.name,
        language_code: selectedTemplate.language_code,
        variables: variables,
        contact_id: selectedContact?.id
      });

      if (response.data.success) {
        onConversationCreated(response.data.conversation_id);
        onClose();
      }
    } catch (error) {
      console.error('Error sending template message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Render step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an existing contact or enter a new phone number.
            </Typography>

            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => `${option.name} (${option.phone_number})`}
              value={selectedContact}
              onChange={(_, contact) => {
                setSelectedContact(contact);
                setNewPhoneNumber('');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Contacts"
                  placeholder="Start typing to search..."
                  onChange={(e) => setContactSearch(e.target.value)}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Person sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.phone_number}
                    </Typography>
                  </Box>
                </Box>
              )}
              loading={loading}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">OR</Typography>
            </Divider>

            <TextField
              fullWidth
              label="New Phone Number"
              placeholder="+1234567890"
              value={newPhoneNumber}
              onChange={(e) => {
                setNewPhoneNumber(e.target.value);
                setSelectedContact(null);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                )
              }}
              helperText="Enter phone number with country code"
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                label="Filter by Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="MARKETING">Marketing</MenuItem>
                <MenuItem value="UTILITY">Utility</MenuItem>
                <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card
                    variant={selectedTemplate?.id === template.id ? 'outlined' : 'elevation'}
                    sx={{
                      border: selectedTemplate?.id === template.id ? 2 : 0,
                      borderColor: 'primary.main'
                    }}
                  >
                    <CardActionArea onClick={() => {
                      setSelectedTemplate(template);
                      // Initialize variables
                      const initialVars: Record<string, string> = {};
                      // Fix: Ensure variables is an array
                      const templateVars = Array.isArray(template.variables) 
                        ? template.variables 
                        : [];
                      templateVars.forEach((_, index) => {
                        initialVars[`${index + 1}`] = '';
                      });
                      setVariables(initialVars);
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Description fontSize="small" />
                          <Typography variant="subtitle2">{template.name}</Typography>
                          {selectedTemplate?.id === template.id && (
                            <Check color="primary" fontSize="small" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                          <Chip
                            label={template.category}
                            size="small"
                            color={
                              template.category === 'MARKETING' ? 'primary' :
                              template.category === 'UTILITY' ? 'success' : 'warning'
                            }
                          />
                          <Chip
                            label={template.language_code}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {Array.isArray(template.variables) ? template.variables.length : 0} variable(s)
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {templates.length === 0 && (
              <Alert severity="info">
                No approved templates found. Create templates in Campaign settings.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            {selectedTemplate && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Template:</strong> {selectedTemplate.name}
                  </Typography>
                </Alert>

                <Grid container spacing={2}>
                  {/* Fix: Ensure variables is an array */}
                  {(Array.isArray(selectedTemplate.variables) ? selectedTemplate.variables : []).map((variable, index) => (
                    <Grid item xs={12} key={index}>
                      <TextField
                        fullWidth
                        label={`Variable {{${index + 1}}} - ${variable}`}
                        placeholder={`Enter ${variable}`}
                        value={variables[`${index + 1}`] || ''}
                        onChange={(e) =>
                          setVariables(prev => ({
                            ...prev,
                            [`${index + 1}`]: e.target.value
                          }))
                        }
                      />
                    </Grid>
                  ))}
                </Grid>

                {(!Array.isArray(selectedTemplate.variables) || selectedTemplate.variables.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    This template has no variables to configure.
                  </Typography>
                )}
              </>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Review & Send
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Recipient
              </Typography>
              <Typography variant="body2">
                {selectedContact?.name || 'New Contact'} ({selectedContact?.phone_number || newPhoneNumber})
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Template
              </Typography>
              <Typography variant="body2">
                {selectedTemplate?.name} ({selectedTemplate?.category})
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Preview fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  Message Preview
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {messagePreview || 'No preview available'}
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Message color="primary" />
          Start WhatsApp Conversation
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {renderStepContent(index)}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          endIcon={activeStep === steps.length - 1 ? <Send /> : <ArrowForward />}
          onClick={handleNext}
          disabled={!canProceed() || sending}
        >
          {sending ? (
            <CircularProgress size={20} />
          ) : activeStep === steps.length - 1 ? (
            'Send Message'
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationInitiator;
